// German date and time fields (dd.mm.yyyy, 24 h, calendar starting on Monday) – the browser's own pickers
// follow the browser language (often English, Sunday first, AM/PM).
import { useEffect, useId, useRef, useState } from 'react';
import { MON, ymd } from './lib/domain';
import { monthGrid, parseDay, shiftMonth, type Month } from './lib/calendar';
import { formatDeDate, parseDeDate, parseTime } from './lib/datetime';
import { matchPeople, pickLabel, type PickPerson } from './lib/lists';
import { Icon } from './ui';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

/** Text shown in the field: follows `value` when it changes from outside, keeps what the user is typing. */
function useTypedText(value: string, format: (v: string) => string) {
  const [text, setText] = useState(format(value));
  const [shown, setShown] = useState(value);
  if (value !== shown) {
    setShown(value);
    setText(format(value));
  }
  return { text, setText, setShown };
}

/** Open/close behaviour of a picker: closes on click outside, Escape and Tab out; focus returns to its button. */
function usePopover() {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) requestAnimationFrame(() => toggle.current?.focus());
  };
  useEffect(() => {
    if (!open) return;
    const onMouse = (ev: MouseEvent) => !box.current?.contains(ev.target as Node) && setOpen(false);
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        setOpen(false);
        requestAnimationFrame(() => toggle.current?.focus());
      }
    };
    const onFocus = (ev: FocusEvent) => !box.current?.contains(ev.target as Node) && setOpen(false);
    document.addEventListener('mousedown', onMouse);
    document.addEventListener('keydown', onKey);
    document.addEventListener('focusin', onFocus);
    return () => {
      document.removeEventListener('mousedown', onMouse);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('focusin', onFocus);
    };
  }, [open]);
  return { open, setOpen, close, box, toggle };
}

export function DateField({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string; // yyyy-mm-dd, '' while the typed text is not a valid date
  onChange: (iso: string) => void;
  required?: boolean;
}) {
  const { text, setText, setShown } = useTypedText(value, formatDeDate);
  const [invalid, setInvalid] = useState(false);
  const { open, setOpen, close, box, toggle } = usePopover();
  const emit = (iso: string) => {
    setShown(iso);
    onChange(iso);
  };
  return (
    <div className="fl date-fl" ref={box}>
      <label>
        <span>{label}</span>
        <span className="dt-field">
          <input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            placeholder="TT.MM.JJJJ"
            value={text}
            required={required}
            aria-invalid={invalid || undefined}
            onChange={e => {
              setText(e.target.value);
              const iso = parseDeDate(e.target.value);
              setInvalid(false);
              emit(iso ?? '');
            }}
            onBlur={() => {
              const iso = parseDeDate(text);
              if (iso) setText(formatDeDate(iso));
              setInvalid(!!text.trim() && !iso);
            }}
          />
        </span>
      </label>
      <button
        ref={toggle}
        type="button"
        className="dt-btn"
        aria-label={`${label}: Kalender öffnen`}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Icon name="cal" sw={1.8} />
      </button>
      {invalid && <small className="dt-err">Bitte als TT.MM.JJJJ eingeben, z. B. 24.12.2026</small>}
      {open && (
        <DatePopover
          value={value}
          onPick={iso => {
            setText(formatDeDate(iso));
            setInvalid(false);
            emit(iso);
            close(true);
          }}
        />
      )}
    </div>
  );
}

function DatePopover({ value, onPick }: { value: string; onPick: (iso: string) => void }) {
  const start = parseDay(value) ?? new Date();
  const [month, setMonth] = useState<Month>({ y: start.getFullYear(), m: start.getMonth() });
  const today = ymd(new Date());
  return (
    <div className="dt-pop" role="dialog" aria-label="Datum wählen">
      <div className="dt-head">
        <button
          type="button"
          className="icon-btn"
          aria-label="Vorheriger Monat"
          onClick={() => setMonth(shiftMonth(month, -1))}
        >
          <Icon name="left" sw={2} />
        </button>
        <b>
          {MON[month.m]} {month.y}
        </b>
        <button
          type="button"
          className="icon-btn"
          aria-label="Nächster Monat"
          onClick={() => setMonth(shiftMonth(month, 1))}
        >
          <Icon name="right" sw={2} />
        </button>
      </div>
      <div className="dt-grid">
        {WEEKDAYS.map(d => (
          <span key={d} className="dt-wd">
            {d}
          </span>
        ))}
        {monthGrid(month).map(d => {
          const k = ymd(d);
          const cls = ['dt-day'];
          if (d.getMonth() !== month.m) cls.push('out');
          if (k === today) cls.push('today');
          if (k === value) cls.push('sel');
          return (
            <button
              key={k}
              type="button"
              className={cls.join(' ')}
              aria-pressed={k === value}
              aria-label={`${d.getDate()}. ${MON[d.getMonth()]} ${d.getFullYear()}`}
              autoFocus={k === value}
              onClick={() => onPick(k)}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function TimeField({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string; // HH:MM (24 h), '' while invalid
  onChange: (hhmm: string) => void;
  required?: boolean;
}) {
  const { text, setText, setShown } = useTypedText(value, v => v);
  const [invalid, setInvalid] = useState(false);
  const { open, setOpen, close, box, toggle } = usePopover();
  const emit = (t: string) => {
    setShown(t);
    onChange(t);
  };
  return (
    <div className="fl date-fl" ref={box}>
      <label>
        <span>{label}</span>
        <span className="dt-field">
          <input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            placeholder="HH:MM"
            maxLength={5}
            value={text}
            required={required}
            aria-invalid={invalid || undefined}
            onChange={e => {
              setText(e.target.value);
              setInvalid(false);
              emit(parseTime(e.target.value) ?? '');
            }}
            onBlur={() => {
              const t = parseTime(text);
              if (t) setText(t);
              setInvalid(!!text.trim() && !t);
            }}
          />
        </span>
      </label>
      <button
        ref={toggle}
        type="button"
        className="dt-btn"
        aria-label={`${label}: Uhrzeit wählen`}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Icon name="clock" sw={1.8} />
      </button>
      {invalid && <small className="dt-err">Bitte als HH:MM eingeben, z. B. 18:30</small>}
      {open && (
        <TimePopover
          value={value}
          onPick={(t, done) => {
            setText(t);
            setInvalid(false);
            emit(t);
            if (done) close(true);
          }}
        />
      )}
    </div>
  );
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);
const two = (n: number) => String(n).padStart(2, '0');

/** Hours 00–23 and minutes in 5-minute steps: an hour keeps the minutes, a minute finishes the choice. */
function TimePopover({ value, onPick }: { value: string; onPick: (t: string, done: boolean) => void }) {
  const [h, m] = (/^(\d{2}):(\d{2})$/.exec(value) ?? [null, '18', '00']).slice(1).map(Number);
  return (
    <div className="dt-pop tm-pop" role="dialog" aria-label="Uhrzeit wählen">
      <p className="tm-h">Stunde</p>
      <div className="tm-grid tm-hours">
        {HOURS.map(x => (
          <button
            key={x}
            type="button"
            className={`dt-day${x === h ? ' sel' : ''}`}
            aria-pressed={x === h}
            aria-label={`${two(x)} Uhr`}
            autoFocus={x === h}
            onClick={() => onPick(`${two(x)}:${two(m)}`, false)}
          >
            {two(x)}
          </button>
        ))}
      </div>
      <p className="tm-h">Minute</p>
      <div className="tm-grid tm-minutes">
        {MINUTES.map(x => (
          <button
            key={x}
            type="button"
            className={`dt-day${x === m ? ' sel' : ''}`}
            aria-pressed={x === m}
            aria-label={`${two(h)}:${two(x)}`}
            onClick={() => onPick(`${two(h)}:${two(x)}`, true)}
          >
            :{two(x)}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Person picker with search: type the start of a first or last name, choose with ↑↓ Enter or a tap.
 * Alphabetical by first name ("Vorname Nachname"). `value` = chosen person id (null: none); `note` adds e.g. "(ehemalig)".
 */
export function PersonPicker<T extends PickPerson & { note?: string }>({
  people,
  value,
  onChange,
  label,
  placeholder = 'Name eintippen …',
  hideLabel = false,
}: {
  people: T[];
  value: number | null;
  onChange: (id: number | null) => void;
  label: string;
  placeholder?: string;
  hideLabel?: boolean;
}) {
  const chosen = people.find(p => p.id === value) ?? null;
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const id = `pp${useId().replace(/:/g, '')}`;
  const MAX = 50;
  const hits = matchPeople(people, q, MAX);
  const more = !q.trim() && people.length > MAX; // a long list: typing finds the rest
  const list = useRef<HTMLUListElement>(null);
  useEffect(() => {
    if (open) list.current?.querySelector('.is-active')?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);
  const pick = (p: T | null) => {
    onChange(p ? p.id : null);
    setQ('');
    setOpen(false);
  };
  const text = open || !chosen ? q : `${pickLabel(chosen)}${chosen.note ? ` ${chosen.note}` : ''}`;
  return (
    <div
      className="fl picker"
      ref={box}
      onBlur={e => {
        // Tab away: close without choosing anything
        if (!box.current?.contains(e.relatedTarget)) {
          setOpen(false);
          setQ('');
        }
      }}
    >
      <label htmlFor={`${id}-in`} className={hideLabel ? 'sr-only' : undefined}>
        {label}
      </label>
      <div className="picker-field">
        <input
          id={`${id}-in`}
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          aria-activedescendant={open && hits[active] ? `${id}-${hits[active].id}` : undefined}
          autoComplete="off"
          placeholder={chosen ? undefined : placeholder}
          value={text}
          onFocus={() => {
            setOpen(true);
            setActive(0);
          }}
          onChange={e => {
            setQ(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onKeyDown={e => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              if (!open) {
                setOpen(true);
                setActive(0);
              } else setActive(a => Math.max(0, Math.min(a + 1, hits.length - 1)));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive(a => Math.max(a - 1, 0));
            } else if (e.key === 'Enter' && open && hits[active]) {
              e.preventDefault();
              pick(hits[active]);
            } else if (e.key === 'Escape') {
              setOpen(false);
              setQ('');
            }
          }}
        />
        {chosen && (
          <button
            type="button"
            className="picker-clear"
            aria-label={`${label}: Auswahl entfernen`}
            onClick={() => pick(null)}
          >
            <Icon name="x" sw={2} />
          </button>
        )}
      </div>
      {open && (
        <ul className="picker-list" id={`${id}-list`} role="listbox" aria-label={label} ref={list}>
          {hits.length
            ? hits.map((p, i) => (
                <li
                  key={p.id}
                  id={`${id}-${p.id}`}
                  role="option"
                  aria-selected={i === active}
                  className={i === active ? 'is-active' : undefined}
                  onPointerDown={e => e.preventDefault()} // keep the focus in the input
                  onClick={() => pick(p)}
                  onPointerEnter={() => setActive(i)}
                >
                  {pickLabel(p)}
                  {p.note && <small> {p.note}</small>}
                </li>
              ))
            : null}
        </ul>
      )}
      {open && (!hits.length || more) && (
        <p className="picker-none" aria-live="polite">
          {hits.length ? `Weitere Namen: weitertippen …` : 'Niemand gefunden'}
        </p>
      )}
    </div>
  );
}
